import axios from 'axios';
import swal from 'sweetalert2';
import { ROOT_URL, getAuthHeaders, isUnauthorizedError, S3_BUCKET, S3_PREFIX_EXAM_ATTACHMENTS, S3_PREFIX_MAIL_ATTACHMENTS, uploadFileViaS3 } from './constants';

// Constantes para el manejo de textos (documentos adjuntos) de las preguntas

// Actualiza los textos adjuntos disponibles en el estado
export const GET_TEXTS = 'GET_TEXTS';

// Actualiza la información del texto adjunto actual en el estado
export const GET_TEXT = 'GET_TEXT';

// Remueve un texto adjunto dado del estado
export const DELETE_TEXT = 'DELETE_TEXT';

/**
 * Obtiene los textos adjuntos disponibles.
 *
 * @export getTexts
 * @param pagination : Opciones de paginación.
 * @param {string} [attachmentType] : Tipo de adjunto a filtrar ('exam' o 'email').
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
export function getTexts(pagination, attachmentType = null, successCallback = () => {}, errorCallback = (error) => {}) {
  if (pagination===undefined){
    pagination =""
  }
  
  // Construir URL con parámetros
  let url = `${ROOT_URL}/texts${pagination}`;
  if (attachmentType) {
    const separator = pagination.includes('?') ? '&' : '?';
    url += `${separator}attachment_type=${attachmentType}`;
  }
  
  const request = axios.get(url, getAuthHeaders());

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_TEXTS,
        payload: data
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        errorCallback(error);
      }
    });
  }
}

/**
 * Crea un nuevo texto adjunto.
 *
 * @export createText
 * @param {*} textInfo : Información del texto adjunto a crear
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
export function createText(textInfo, successCallback = () => {}, errorCallback = (error) => {}) {
  const request = axios.post(`${ROOT_URL}/texts`, textInfo, getAuthHeaders());

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_TEXT,
        payload: data
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        errorCallback(error);
      }
    });
  }
}

/**
 * Elimina un texto adjunto con el id dado por parámetro.
 * Elimina el archivo en S3 y luego el registro de la BD.
 *
 * @export deleteText
 * @param {*} id : EL id del texto adjunto.
 * @param {string} attachmentType : Tipo de adjunto ('exam' o 'email') para determinar el contenedor.
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
export function deleteText(id, attachmentType = 'exam', successCallback = () => {}, errorCallback = (error) => {}) {
  const prefix = attachmentType === 'email'
    ? S3_PREFIX_MAIL_ATTACHMENTS
    : S3_PREFIX_EXAM_ATTACHMENTS;

  const request = axios.delete(
    `${ROOT_URL}/texts/${id}/delete_text?bucket=${encodeURIComponent(S3_BUCKET)}&prefix=${encodeURIComponent(prefix)}`,
    getAuthHeaders()
  );

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: DELETE_TEXT,
        payload: id
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        errorCallback(error);
      }
    });
  }
}


/**
 * Obtiene la URL presignada de S3 para un archivo de texto específico.
 *
 * @export getTextFileUrl
 * @param {*} id : El id del texto adjunto.
 * @param {*} filename : Nombre del archivo almacenado en S3.
 * @param {*} prefix : Prefijo/carpeta dentro del bucket S3.
 * @returns Promise que resuelve con la URL del archivo.
 */
export function getTextFileUrl(id, filename, prefix) {
  const ip = sessionStorage.getItem('ip_add') || '';

  return axios.get(
    `${ROOT_URL}/texts/${id}/file_url?bucket=${encodeURIComponent(S3_BUCKET)}&prefix=${encodeURIComponent(prefix)}&sip=${ip}`,
    getAuthHeaders()
  ).then((response) => response.data.url).catch((error) => {
    console.error('Error obteniendo URL del texto:', error);
    throw error;
  });
}

/**
 * Sube un archivo de texto adjunto a S3.
 *
 * @export uploadTextFile
 */
export async function uploadTextFile(file, filename, prefix, onUploadProgress) {
  try {
    return await uploadFileViaS3('/texts/upload_url', file, filename, S3_BUCKET, prefix, undefined, onUploadProgress);
  } catch (error) {
    console.error('Error subiendo archivo de texto:', error);
    swal({
      type: 'error',
      title: 'Error',
      text: 'No se pudo subir el archivo. Por favor intente nuevamente.',
    });
    throw error;
  }
}